import { ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn, Icon, Skeleton } from "@nexus/ui";
import type { VertexUsage } from "@/features/llm-usage/api/llm-usage.api";
import { formatTokensCompact, formatTokensExact, formatTokenShare } from "../lib/format-tokens";
import { brandForModelId, humanizeModelId } from "../lib/model-display";
import { isLongTail } from "../lib/table-filters";
import { SECONDARY_LIST_MAX_HEIGHT } from "../lib/table-scroll";
import { summarizeVertexReconciliation, type RecordedLlmUsageForVertex } from "../lib/vertex-reconciliation";
import { rollUpVertexModels, type VertexModelRollup } from "../lib/vertex-model-rollup";
import { LongTailChip } from "./LongTailChip";
import { ProviderIcon } from "./ProviderIcon";

interface VertexReconciliationPanelProps {
  data: VertexUsage | undefined;
  isLoading: boolean;
  recorded: RecordedLlmUsageForVertex;
  /** `tokenUsageLongTailPercent`: the share a row needs to escape the fold. */
  longTailPercent: number;
}

const NOTE_CLASS = "text-[11px] text-muted-foreground";

/**
 * What Vertex billed, next to what we recorded.
 *
 * The tables on the page attribute spend to a tenant and a feature; this says
 * whether that attribution accounts for the whole bill. A gap is not
 * necessarily a bug — it is calls made outside Nexus (Vertex AI Studio, ad-hoc
 * scripts) or calls made before this instrumentation shipped — but it is the
 * number that tells an operator how much of the bill the breakdown explains.
 *
 * Rendered as the body of `VertexReconciliationDialog`, which is why it carries
 * no surface of its own.
 */
export function VertexReconciliationPanel({
  data,
  isLoading,
  recorded,
  longTailPercent,
}: VertexReconciliationPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [hideLongTail, setHideLongTail] = useState(false);
  const [expandedModels, setExpandedModels] = useState<ReadonlySet<string>>(new Set());

  const rows = useMemo(() => rollUpVertexModels(data?.byModel ?? []), [data?.byModel]);
  const summary = useMemo(
    () => summarizeVertexReconciliation(data, recorded),
    [data, recorded],
  );
  const longTailCount = useMemo(
    () =>
      rows.filter((row) => isLongTail(row.totalTokens, summary.vertexTotal, longTailPercent))
        .length,
    [rows, summary.vertexTotal, longTailPercent],
  );
  const visibleRows = useMemo(
    () =>
      hideLongTail
        ? rows.filter((row) => !isLongTail(row.totalTokens, summary.vertexTotal, longTailPercent))
        : rows,
    [rows, hideLongTail, summary.vertexTotal, longTailPercent],
  );

  if (isLoading && !data) {
    return <Skeleton className="h-24 w-full rounded-md bg-foreground/8" />;
  }

  if (summary.status === "unavailable") {
    return (
      <div className="flex items-start gap-2">
        <Icon name="info" size="xs" className="mt-0.5 text-muted-foreground" aria-hidden />
        <p className={NOTE_CLASS}>
          {t(
            "admin.llmUsage.vertex.unavailable",
            "Cloud Monitoring is not readable from this environment, so there is nothing to reconcile against. The numbers above are what Nexus recorded.",
          )}
        </p>
      </div>
    );
  }

  const gapShare = formatTokenShare(summary.gapTokens, summary.vertexTotal, locale);

  // A number nobody can explain gets escalated. Naming the ordinary cause of
  // each direction is what turns this from an anomaly into a reading.
  const explanation =
    summary.status === "gap"
      ? t(
          "admin.llmUsage.vertex.gapExplainer",
          "Something else on this Google Cloud project is using Vertex: staging, a PR environment, a script on someone's laptop, or the Vertex console. It is billed here but never reaches the tables on this page.",
        )
      : summary.status === "shortfall"
        ? t(
            "admin.llmUsage.vertex.shortfallExplainer",
            "Nexus counted more than Google has reported back. Cloud Monitoring publishes on a delay, so the last stretch of any window is still filling in — this usually settles on its own.",
          )
        : t(
            "admin.llmUsage.vertex.reconciledExplainer",
            "Everything Google billed this project for is accounted for in the tables on this page.",
          );

  const toggleModel = (key: string) =>
    setExpandedModels((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="flex flex-col gap-1">
        {/* The share is what makes the gap legible: 4.4M means nothing until it
            is most of the bill. */}
        <p
          className="text-xs text-foreground"
          title={formatTokensExact(summary.vertexTotal, locale)}
        >
          {summary.status === "gap" && gapShare
            ? t(
                "admin.llmUsage.vertex.gapDetail",
                "{{share}} of the {{tokens}} Vertex reported in this window",
                { share: gapShare, tokens: formatTokensCompact(summary.vertexTotal, locale) },
              )
            : t("admin.llmUsage.vertex.windowTotal", "{{tokens}} reported in this window", {
                tokens: formatTokensCompact(summary.vertexTotal, locale),
              })}
        </p>
        <p className={NOTE_CLASS}>{explanation}</p>
        {summary.residualTokens > 0 ? (
          <p className={NOTE_CLASS}>
            {t(
              "admin.llmUsage.vertex.residual",
              "{{tokens}} of what Nexus recorded is thinking or cache, which Vertex's input/output metrics do not include.",
              { tokens: formatTokensCompact(summary.residualTokens, locale) },
            )}
          </p>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className={NOTE_CLASS}>
          {t("admin.llmUsage.vertex.empty", "Vertex reported no traffic in this window.")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          {longTailCount > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-2">
              <LongTailChip
                active={hideLongTail}
                onToggle={() => setHideLongTail((prev) => !prev)}
                hiddenCount={longTailCount}
                thresholdPercent={longTailPercent}
              />
            </div>
          ) : null}
          <ul className={cn(SECONDARY_LIST_MAX_HEIGHT, "divide-y divide-border overflow-y-auto")}>
            {visibleRows.map((row) => (
              <VertexModelRow
                key={`${row.publisher}-${row.model}`}
                row={row}
                vertexTotal={summary.vertexTotal}
                locale={locale}
                isExpanded={expandedModels.has(`${row.publisher}-${row.model}`)}
                onToggle={() => toggleModel(`${row.publisher}-${row.model}`)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface VertexModelRowProps {
  row: VertexModelRollup;
  vertexTotal: number;
  locale: string;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * One model, with its regions a click away.
 *
 * Region only earns a line of its own when there is more than one of them —
 * for everything else it is a label on a row nobody is comparing across
 * regions anyway.
 */
function VertexModelRow({ row, vertexTotal, locale, isExpanded, onToggle }: VertexModelRowProps) {
  const { t } = useTranslation();
  const share = formatTokenShare(row.totalTokens, vertexTotal, locale);
  const modelName = humanizeModelId(row.model);
  const isSplit = row.regions.length > 1;

  return (
    <li className="text-xs">
      <div className="flex items-center gap-3 px-3 py-1.5">
        <span className="flex min-w-0 flex-1 items-center gap-2" title={row.model}>
          <ProviderIcon brand={brandForModelId(row.model)} />
          <span className="truncate font-medium">{modelName}</span>
        </span>

        {isSplit ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isExpanded}
            className="flex items-center gap-1 rounded-sm text-[11px] text-muted-foreground transition-colors duration-150 ease-out hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={t(
              "admin.llmUsage.vertex.toggleRegions",
              "Show the regions {{model}} was served from",
              { model: modelName },
            )}
          >
            <ChevronRight
              className={cn(
                "h-3 w-3 transition-transform duration-150 ease-out",
                isExpanded && "rotate-90",
              )}
              aria-hidden
            />
            {t("admin.llmUsage.vertex.regions", {
              count: row.regions.length,
              defaultValue_one: "{{count}} region",
              defaultValue_other: "{{count}} regions",
            })}
          </button>
        ) : (
          <span className={NOTE_CLASS}>{row.regions[0]?.location}</span>
        )}

        <span
          className="flex w-28 items-baseline justify-end gap-1.5 tabular-nums"
          title={formatTokensExact(row.totalTokens, locale)}
        >
          <span className="text-foreground">{formatTokensCompact(row.totalTokens, locale)}</span>
          {share ? <span className="text-[11px] text-muted-foreground">{share}</span> : null}
        </span>
      </div>

      {isSplit && isExpanded ? (
        <ul className="border-t border-border bg-muted/40 py-1 pl-9 pr-3">
          {row.regions.map((region) => (
            <li
              key={region.location}
              className="flex items-center gap-3 py-0.5 text-[11px] text-muted-foreground"
            >
              <span className="min-w-0 flex-1 truncate">{region.location}</span>
              <span
                className="w-28 text-right tabular-nums"
                title={formatTokensExact(region.totalTokens, locale)}
              >
                {formatTokensCompact(region.totalTokens, locale)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}
